import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";

interface DocumentScanLoaderProps {
  size?: number;
  steps?: string[];
}

const DEFAULT_STEPS = [
  "Reading document...",
  "Scanning content...",
  "Extracting data...",
  "Generating response...",
];

const STEP_DURATION_MS = 20000;

const DocumentScanLoader: React.FC<DocumentScanLoaderProps> = ({
  size = 96,
  steps = DEFAULT_STEPS,
}) => {
  const theme: Theme = useTheme();
  const [stepIndex, setStepIndex] = useState(0);

  // Advance one step at a time and stay on the final step once reached.
  useEffect(() => {
    if (stepIndex >= steps.length - 1) {
      return;
    }
    const timer = setTimeout(
      () => setStepIndex((prev) => Math.min(prev + 1, steps.length - 1)),
      STEP_DURATION_MS
    );
    return () => clearTimeout(timer);
  }, [stepIndex, steps.length]);

  const width = size;
  const height = Math.round(size * 1.28);

  const primary = theme.colors_ds.PRIMARY_ICON_MAIN;
  const pageBg = theme.colors_ds.NEUTRAL_BG_MAIN;
  const pageBorder = theme.colors_ds.NEUTRAL_BORDER_DEFAULT;
  const lineColor = theme.colors_ds.NEUTRAL_BORDER_WEAK;

  return (
    <div style={styles.wrapper}>
      <div style={styles.scanArea(width, height)}>
        <svg
          width={width}
          height={height}
          viewBox="0 0 96 123"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ display: "block" }}
        >
          {/* Page body with a folded top-right corner */}
          <path
            d="M8 4C8 1.79086 9.79086 0 12 0H64L88 24V119C88 121.209 86.2091 123 84 123H12C9.79086 123 8 121.209 8 119V4Z"
            fill={pageBg}
            stroke={pageBorder}
            strokeWidth="2"
          />
          {/* Folded corner */}
          <path
            d="M64 0L88 24H68C65.7909 24 64 22.2091 64 20V0Z"
            fill={lineColor}
          />
          {/* Text lines */}
          <rect x="20" y="40" width="56" height="5" rx="2.5" fill={lineColor} />
          <rect x="20" y="54" width="56" height="5" rx="2.5" fill={lineColor} />
          <rect x="20" y="68" width="40" height="5" rx="2.5" fill={lineColor} />
          <rect x="20" y="86" width="56" height="5" rx="2.5" fill={lineColor} />
          <rect
            x="20"
            y="100"
            width="32"
            height="5"
            rx="2.5"
            fill={lineColor}
          />
        </svg>

        {/* Sweeping scan line */}
        <motion.div
          style={styles.scanLine(primary)}
          animate={{ top: ["12%", "82%", "12%"] }}
          transition={{ duration: 2.4, ease: "easeInOut", repeat: Infinity }}
        />
      </div>

      <div style={styles.caption(theme)}>
        <AnimatePresence mode="wait">
          <motion.div
            key={stepIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
          >
            {steps[stepIndex]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DocumentScanLoader;

const styles = {
  wrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
  },
  scanArea: (width: number, height: number) => ({
    position: "relative" as "relative",
    width,
    height,
    overflow: "hidden" as "hidden",
  }),
  scanLine: (primary: string) => ({
    position: "absolute" as "absolute",
    left: "8%",
    right: "8%",
    height: "14px",
    borderRadius: "4px",
    background: `linear-gradient(180deg, ${primary}00 0%, ${primary}40 45%, ${primary} 50%, ${primary}40 55%, ${primary}00 100%)`,
    boxShadow: `0 0 12px 2px ${primary}66`,
  }),
  caption: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
    lineHeight: "24px",
    minHeight: "24px",
    marginTop: "16px",
    textAlign: "center" as "center",
  }),
};
